import { Button, Card, Space, Tag, Typography } from 'antd';
import {
  ArrowUpOutlined,
  CheckOutlined,
  CopyOutlined,
  DownloadOutlined,
  FileTextOutlined,
  FullscreenOutlined,
} from '@ant-design/icons';

const { Text, Title } = Typography;

export function SavedArtifactCardView({ accentTheme, artifactTitle, canOpen, cardTheme, icon, onOpen }) {
  return (
    <Button
      aria-label={`打开${artifactTitle}`}
      className="flare-saved-artifact-card"
      disabled={!canOpen}
      onClick={onOpen}
      style={{ width: '100%', height: 'auto', padding: 12, borderRadius: 10,
        border: `1px solid ${cardTheme.assistantBorder}`, background: cardTheme.assistantBackground,
        textAlign: 'left', whiteSpace: 'normal' }}
      type="text"
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minWidth: 0 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flex: '0 0 34px', height: 38, borderRadius: 7, fontSize: 16,
          background: accentTheme.accentSurface, color: accentTheme.accentColor }}>
          {icon}
        </span>
        <span style={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <Text style={{ color: cardTheme.textPrimary, fontWeight: 500, overflowWrap: 'anywhere' }}>
            {artifactTitle}
          </Text>
          <Text style={{ color: cardTheme.textSecondary, fontSize: 12 }}>已保存</Text>
        </span>
        <ArrowUpOutlined rotate={45} style={{ flex: '0 0 auto', color: cardTheme.textPrimary }} />
      </span>
    </Button>
  );
}

const ACTION_BUTTON_STYLE = {
  borderRadius: 9,
  height: 28,
  width: 28,
};
const COPIED_BUTTON_STYLE = {
  ...ACTION_BUTTON_STYLE,
  background: '#f6ffed',
  border: '1px solid #b7eb8f',
  color: '#389e0d',
};
const CARD_HEADER_STYLE = {
  alignItems: 'flex-start',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  minWidth: 0,
};
const CARD_TITLE_ROW_STYLE = {
  alignItems: 'flex-start',
  display: 'flex',
  flex: '1 1 180px',
  gap: 8,
  minWidth: 'min(100%, 180px)',
};
const CARD_TITLE_TEXT_STYLE = {
  margin: 0,
  lineHeight: 1.2,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'keep-all',
};
const CARD_ACTIONS_STYLE = {
  flex: '0 0 auto',
  marginLeft: 'auto',
};

function ArtifactTitleBlock({
  accentBorder,
  accentColor,
  accentSurface,
  artifactTitle,
  cardTheme,
  showArtifactTitle,
  statusMeta,
  title,
}) {
  const displayTitle = showArtifactTitle ? artifactTitle : title;
  return (
    <div style={CARD_TITLE_ROW_STYLE}>
      <div
        style={{
          alignItems: 'center',
          background: accentSurface,
          border: `1px solid ${accentBorder}`,
          borderRadius: 9,
          color: accentColor,
          display: 'inline-flex',
          flex: '0 0 30px',
          height: 30,
          justifyContent: 'center',
          width: 30,
        }}
      >
        <FileTextOutlined />
      </div>
      <div style={{ minWidth: 0 }}>
        <Space size={6} style={{ marginBottom: 1, minWidth: 0 }} wrap>
          <Text style={{ color: cardTheme.textSecondary, fontSize: 11.5, fontWeight: 600 }}>
            文档快照
          </Text>
          <Tag color={statusMeta.color} style={{ lineHeight: '18px', marginInlineEnd: 0 }}>
            {statusMeta.label}
          </Tag>
        </Space>
        <Title level={5} style={{ ...CARD_TITLE_TEXT_STYLE, color: cardTheme.textPrimary }}>
          {displayTitle}
        </Title>
        {showArtifactTitle ? (
          <Text style={{ color: cardTheme.textSecondary, fontSize: 11.5 }}>
            {title}
          </Text>
        ) : null}
      </div>
    </div>
  );
}

function ArtifactActions({
  actionButtonStyle,
  canDownload,
  canOpen,
  copySuccess,
  hasContent,
  onCopy,
  onDownload,
  onOpen,
}) {
  const copyLabel = copySuccess ? '已复制' : '复制';
  const copyIcon = copySuccess ? <CheckOutlined /> : <CopyOutlined />;
  const copyStyle = copySuccess ? COPIED_BUTTON_STYLE : actionButtonStyle;
  return (
    <Space size={4} style={CARD_ACTIONS_STYLE}>
      <Button
        aria-label="打开"
        disabled={!canOpen}
        icon={<FullscreenOutlined />}
        onClick={onOpen}
        size="small"
        style={actionButtonStyle}
        title="打开"
        type="text"
      />
      <Button
        aria-label={copyLabel}
        disabled={!hasContent}
        icon={copyIcon}
        onClick={onCopy}
        size="small"
        style={copyStyle}
        title={copyLabel}
        type="text"
      />
      <Button
        aria-label="下载"
        disabled={!canDownload}
        icon={<DownloadOutlined />}
        onClick={onDownload}
        size="small"
        style={actionButtonStyle}
        title="下载"
        type="text"
      />
    </Space>
  );
}

function ArtifactPreview({ accentBorder, accentSurface, cardTheme, hasContent, preview }) {
  if (!hasContent) {
    return null;
  }
  return (
    <details
      style={{
        background: accentSurface,
        border: `1px solid ${accentBorder}`,
        borderRadius: 9,
        padding: '7px 9px',
      }}
    >
      <summary style={{ color: cardTheme.textSecondary, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
        预览摘要
      </summary>
      <Text style={{ color: cardTheme.textPrimary, display: 'block', fontSize: 12, lineHeight: 1.42, marginTop: 6 }}>
        {preview}
      </Text>
    </details>
  );
}

export function MessageArtifactCardView({
  accentTheme,
  artifactTitle,
  canDownload,
  canOpen,
  cardTheme,
  copySuccess,
  hasContent,
  onCopy,
  onDownload,
  onOpen,
  preview,
  showArtifactTitle,
  statusMeta,
  title,
}) {
  const { accentBorder, accentColor, accentSurface } = accentTheme;
  const actionButtonStyle = {
    ...ACTION_BUTTON_STYLE,
    background: accentSurface,
    border: `1px solid ${accentBorder}`,
    color: accentColor,
  };
  return (
    <Card
      className="flare-message-artifact-card"
      size="small"
      styles={{ body: { padding: 9 } }}
      style={{
        background: cardTheme.assistantBackground,
        borderColor: cardTheme.assistantBorder,
        borderRadius: 11,
      }}
    >
      <Space orientation="vertical" size={8} style={{ minWidth: 0, width: '100%' }}>
        <div style={CARD_HEADER_STYLE}>
          <ArtifactTitleBlock
            accentBorder={accentBorder}
            accentColor={accentColor}
            accentSurface={accentSurface}
            artifactTitle={artifactTitle}
            cardTheme={cardTheme}
            showArtifactTitle={showArtifactTitle}
            statusMeta={statusMeta}
            title={title}
          />
          <ArtifactActions
            actionButtonStyle={actionButtonStyle}
            canDownload={canDownload}
            canOpen={canOpen}
            copySuccess={copySuccess}
            hasContent={hasContent}
            onCopy={onCopy}
            onDownload={onDownload}
            onOpen={onOpen}
          />
        </div>
        <ArtifactPreview
          accentBorder={accentBorder}
          accentSurface={accentSurface}
          cardTheme={cardTheme}
          hasContent={hasContent}
          preview={preview}
        />
      </Space>
    </Card>
  );
}
